<template>
    <div ref="dialogEl" class="search-modal" @click.self="close" role="dialog" aria-modal="true" aria-label="Search">
        <div class="search-modal-content">
            <button type="button" class="search-modal-close" @click="close" aria-label="Close search"><span aria-hidden="true">&times;</span></button>

            <form class="search-form" @submit.prevent="run">
                <label class="sr-only" for="site-search-field">Search Open House Chicago</label>
                <input
                    id="site-search-field"
                    ref="field"
                    v-model="query"
                    type="search"
                    autocomplete="off"
                    placeholder="Search Open House Chicago" />
                <button type="submit" class="search-form-submit">Search</button>
            </form>

            <div class="search-results" aria-live="polite">
                <template v-if="loading">
                    <p class="search-status">Searching…</p>
                </template>

                <template v-else-if="failed">
                    <p class="search-status">Search is unavailable right now. Please try again shortly.</p>
                </template>

                <template v-else-if="hits.length">
                    <GlobalSearchHit
                        v-for="hit in hits"
                        :key="hit.objectID"
                        :hit="hit"
                        @navigate="close" />
                </template>

                <template v-else-if="searched">
                    <p class="search-status">No results for “{{ query }}”. Try a different search.</p>
                </template>

                <template v-else>
                    <p class="search-status">Search sites, neighborhoods, tours and events.</p>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { useSiteSearch } from '@/composables/useSiteSearch';

    const emit = defineEmits(['close']);

    const { query, hits, loading, searched, failed, run } = useSiteSearch();
    const field = ref(null);

    const close = () => emit('close');

    // Debounced so typing doesn't fire a request per keystroke. The composable
    // discards out-of-order responses, so a slow early query can't overwrite a
    // later one even when several are in flight.
    let timer = null;
    watch(query, () => {
        clearTimeout(timer);
        timer = setTimeout(run, 400);
    });

    // Focus starts in the field so the modal is usable from the keyboard
    // immediately; Tab stays inside; Escape closes; background scroll is
    // locked (iOS-safe). On close, focus goes back to the header's search
    // button — it used to be dropped on <body>, sending keyboard and screen
    // reader users back to the top of the page.
    const dialogEl = ref(null);
    useDialog(dialogEl, { onEscape: close, initialFocus: field });

    onUnmounted(() => {
        clearTimeout(timer);
    });
</script>

<style lang="scss" scoped>
    .search-modal {
        position: fixed;
        inset: 0;
        z-index: $zStepFive;
        background: rgba(0, 0, 0, 0.55);
        display: flex;
        align-items: flex-start;
        justify-content: center;
        padding: 16px;
        overflow-y: auto;

        @media (min-width: $tablet) {
            padding: 80px 24px;
        }
    }

    .search-modal-content {
        position: relative;
        width: 100%;
        max-width: 720px;
        background: $white;
        padding: 24px 20px 8px;

        @media (min-width: $tablet) {
            padding: 40px 40px 16px;
        }
    }

    .search-modal-close {
        position: absolute;
        top: 8px;
        right: 12px;
        border: 0;
        background: none;
        font-size: 32px;
        line-height: 1;
        cursor: pointer;
        color: $black;
    }

    .search-form {
        display: flex;
        gap: 8px;

        input {
            flex: 1 1 auto;
            min-width: 0;
            padding: 12px 16px;
            border: 1px solid $black;
            font-family: $body;
            font-size: 16px;
        }
    }

    .search-form-submit {
        flex: 0 0 auto;
        padding: 12px 20px;
        border: 1px solid $black;
        background: $black;
        color: $white;
        font-family: $body;
        font-size: 12px;
        font-weight: $heavy;
        letter-spacing: 0.72px;
        text-transform: uppercase;
        cursor: pointer;
    }

    .search-results {
        margin-top: 20px;
    }

    .search-status {
        font-family: $body;
        font-size: 14px;
        color: $gray-dark;
        padding: 8px 0 16px;
    }
</style>
